import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../redux/store";
import "./UserSelect.css"
import React, {useEffect, useState} from "react";
import RootState from "../../../redux/states/root.state";
import { fetchCommands } from "../../../redux/actions/commands.actions";
import { CircularProgress, Pagination, Box, Button, TextField, Radio, FormControlLabel, List, ListItem, ListItemButton, ListItemText, ListItemIcon, Typography, RadioGroup, FormControl, MenuItem, Select, InputLabel } from "@mui/material";
import useRoleCheck from "../../../hooks/useRoleCheck";
import usePermissionCheck from "../../../hooks/usePermissionCheck";
import { fetchAvailableDevFirmware, fetchAvailableFirmware } from "../../../redux/actions/firmware.actions";
import { palName } from "../../../utils/brand";

interface SelectProps {
    setSelectedCommand: any;
    setSelectedLogLevel: Function;
    setSelectedService: Function;
    setSelectedFirmware: Function;
    setSelectedVersion: Function;
    spas: any[];
}


const services = [
    {
        title: 'USB Monitor',
        value: 'usb_monitor',
    },
    {
        title: 'Serial Transceiver',
        value: 'serial_transceiver',
    },
    {
        title: 'MQTT Handler',
        value: 'mqtt_handler',
    },
    {
        title: 'Bluetooth Handler',
        value: 'bt_handler',
    },
    {
        title: 'Local Broker',
        value: 'local_broker',
    },
    {
        title: 'Spa Service Init',
        value: 'spa-service-init',
    },
    {
        title: 'Spa Heartbeat',
        value: 'spa-heartbeat',
    },
    {
        title: 'Spa MQTT Service',
        value: 'spa-mqtt-service',
    },
    {
        title: 'Spa USB Service',
        value: 'spa-usb-service',
    },
    {
        title: 'Spa Bluetooth Service',
        value: 'spa-bluetooth-service',
    },
    {
        title: 'Spa Serial Service',
        value: 'spa-serial-service',
    },
    {
        title: 'Spa MQTT Broker Service',
        value: 'spa-mqtt-broker-service',
    },
    {
        title: 'Spa Arctic Service',
        value: 'spa-arctic-service',
    },
    {
        title: 'Spa Network Service',
        value: 'spa-network-service',
    },
    {
        title: 'Spa Website Service',
        value: 'spa-website-service',
    },
    {
        title: 'Spa WebSocket Service',
        value: 'spa-websocket-service',
    },
    {
        title: 'Spa Commands Service',
        value: 'spa-commands-service',
    },
    {
        title: 'Spa Register Service',
        value: 'spa-register-service',
    },
    {
        title: 'Spa Tailscale Service',
        value: 'spa-tailscale-service',
    },
    {
        title: 'Spa Config Init',
        value: 'spa-config-init',
    },
    {
        title: 'Spa Version Update',
        value: 'spa-version-update',
    },
];

const logLevels = [
    {
        title: 'OFF',
        value: 'CRITICAL',
    },
    {
        title: 'DEBUG',
        value: 'DEBUG',
    },
    {
        title: 'INFO',
        value: 'INFO',
    },
    {
        title: 'WARNING',
        value: 'WARNING',
    },
    {
        title: 'ERROR',
        value: 'ERROR',
    },
]

const CommandSelect: React.FC<SelectProps> = ({ setSelectedCommand, setSelectedLogLevel, setSelectedService, setSelectedFirmware, setSelectedVersion, spas}) => {
const dispatch: AppDispatch = useDispatch();
const {data:commands, meta, loading} = useSelector((state: RootState) => state.commands);
const [pageNumber, setPageNumber] = useState(1);
const [title, setTitle] = useState(``);
const [query, setQuery] = useState<any>(null);
const [logLevel, setLogLevel] = useState('INFO');
const [service, setService] = useState('');
const [firmware, setFirmware] = useState('');
const [version, setVersion] = useState('');

const [submenuOpened, setSubmenuOpened] = useState(false);
const [serviceSubmenuOpened, setServiceSubmenuOpened] = useState(false);
const [firmwareSubmenuOpened, setFirmwareSubmenuOpened] = useState(false);
const [upgradeFirmwareSubmenuOpened, setUpgradeFirmwareSubmenuOpened] = useState(false);

const {data: availableFirmware, loading: firmwareLoading} = useSelector((state: RootState)=>state.firmware);

const [selectedCommand, setLocalSelectedCommand] = useState<any>(null);

const {canAccess: isDeveloper} = useRoleCheck(['developer']);
const {canAccess: canAccessDevFirmware} = usePermissionCheck(['view_dev_firmware']);

const [activeTab, setActiveTab] = useState<'public' | 'dev'>('public');

useEffect(() => {
  if (activeTab === 'public') {
    dispatch(fetchAvailableFirmware(""));
} else {
    dispatch(fetchAvailableDevFirmware(""));
}
}, [dispatch, activeTab]);

// Command changed:
const commandSelected = (command: { title: string; }) => {
    setLocalSelectedCommand(command);
    setSelectedCommand(command);

    setSubmenuOpened(command.title === "update_log_level");
    setServiceSubmenuOpened(command.title === "restart_single_service");
    setFirmwareSubmenuOpened(command.title === "upgrade_yocto");
    setUpgradeFirmwareSubmenuOpened(command.title === "upgrade_firmware");

    if (command.title === "upgrade_yocto") {
        firmwareSelected('yocto');
    } else {
        firmwareSelected('');
    }
}

const logLevelSelected = (level: React.SetStateAction<string>) => {
    setSelectedLogLevel(level);
    setLogLevel(level)
}

const serviceSelected = (service: React.SetStateAction<string>) => {
    setSelectedService(service);
    setService(service)
}

const firmwareSelected = (firmware: React.SetStateAction<string>) => {
    setSelectedFirmware(firmware);
    setFirmware(firmware)
}

const versionSelected = (version: React.SetStateAction<string>) => {
    setSelectedVersion(version);
    setVersion(version)
}

useEffect(() => {
    let param = `page=${pageNumber}`;
    if (query){
        param = query + `&title=${query}`
    }
    dispatch(fetchCommands(param))
  }, [dispatch,pageNumber, query]);

  useEffect(() => {
    if (spas.length === 1 && spas[0]?.log_level) {
        setLogLevel(spas[0].log_level);
        setSelectedLogLevel(spas[0].log_level);
    }
  }, [spas]);


  return (
    <>
    <div className="user-search-container">
        <TextField type="search" placeholder="Search title..." onChange={(e)=>setTitle(e.target.value)} disabled={commands.length===0} size="small" fullWidth/>
        <Button type="button" variant="contained" onClick={()=>setQuery(title)} disabled={commands.length===0} size="small">Search</Button>
    </div>

    {loading ?
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
        </Box>
        :
        commands.length>0 ? 
            <div className="select-container">
                <RadioGroup
                    value={selectedCommand?.id || ''}
                    onChange={(e) => {
                        const command = commands.find(c => c.id === e.target.value);
                        if (command) commandSelected(command);
                    }}
                >
                    <List 
                        dense
                        sx={{
                            maxHeight: 200,
                            overflow: 'auto',
                            '&::-webkit-scrollbar': {
                                width: '8px',
                            },
                            '&::-webkit-scrollbar-track': {
                                backgroundColor: 'rgba(0,0,0,0.1)',
                                borderRadius: '4px',
                            },
                            '&::-webkit-scrollbar-thumb': {
                                backgroundColor: 'rgba(0,0,0,0.3)',
                                borderRadius: '4px',
                                '&:hover': {
                                    backgroundColor: 'rgba(0,0,0,0.5)',
                                },
                            },
                        }}
                    >
                    {commands.map((command: any) => {
                       if (command?.title === 'update_user' || command?.title === 'update_dealer' || command?.title === 'model_changed') return null;
                       if (!isDeveloper && (command?.title === 'update_firmware' || command?.title === 'upload_logs' || command?.title === 'upgrade_yocto' || command?.title === 'upgrade_firmware' || command?.title === 'open_ssh_tunnel' || command?.title === 'activate_active_api_mode' || command?.title === 'deactivate_active_api_mode')) return null;
                       return (
                       <ListItem key={command.id} disablePadding>
                            <ListItemButton
                                onClick={() => commandSelected(command)}
                                sx={{ borderRadius: 1}}
                            >
                                <ListItemIcon>
                                    <Radio value={command.id} size="small"/>
                                </ListItemIcon>
                                <ListItemText
                                    primary={
                                        <Typography variant="body2">
                                            {command?.title === 'serial' ? 'toggle lights' : command?.title?.replaceAll('_', ' ')}
                                        </Typography>
                                    }
                                    secondary={
                                        upgradeFirmwareSubmenuOpened && command?.title === 'upgrade_firmware'
                                            ? <Typography variant="caption" color="text.secondary">Only supported by Yocto versions &gt;= 3.1.54</Typography>
                                            : undefined
                                    }
                                />
                            </ListItemButton>
                            {
                                submenuOpened && command?.title === 'update_log_level' &&
                                <Box sx={{ ml: 4, mt: 1 }}>
                                    <RadioGroup
                                        value={logLevel}
                                        onChange={(e) => logLevelSelected(e.target.value)}
                                        row
                                    >
                                        {
                                            logLevels.map((level: any) => (
                                                <FormControlLabel
                                                    key={level.value}
                                                    value={level.value}
                                                    control={<Radio size="small"/>}
                                                    label={<Typography variant="body2">{level.title}</Typography>}
                                                />
                                            ))
                                        }
                                    </RadioGroup>
                                </Box>
                            }
                            {
                                serviceSubmenuOpened && command?.title === 'restart_single_service' &&
                                <Box sx={{ mr: 4, mt: 1, display: 'flex', flexDirection: 'row', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                                    <FormControl sx={{ minWidth: 120, flex: 1 }}>
                                    <InputLabel>Service</InputLabel>
                                    <Select
                                        value={service}
                                        onChange={(e) => serviceSelected(e.target.value)}
                                    >
                                        {
                                            services.map((toRestartService: any) => (
                                                <MenuItem
                                                    key={toRestartService.value}
                                                    value={toRestartService.value}
                                                >
                                                    {toRestartService.title}
                                                </MenuItem>
                                            ))
                                        }
                                    </Select>
                                    </FormControl>
                

                                </Box>
                            }
                            {
                                firmwareSubmenuOpened && command?.title === 'upgrade_yocto' &&
                                <Box sx={{ mr: 4, mt: 1, display: 'flex', flexDirection: 'row', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                                    <FormControl sx={{ minWidth: 120, flex: 1 }}>
                                        <InputLabel>Firmware</InputLabel>
                                        <Select
                                            value={'yocto'}
                                            disabled
                                        >
                                            <MenuItem value={'yocto'}>Yocto</MenuItem>
                                        </Select>
                                    </FormControl>

                                    {firmware !== '' && (
                                        <FormControl sx={{ minWidth: 120, flex: 1 }}>
                                            <InputLabel>Visibility</InputLabel>
                                            <Select
                                                value={activeTab}
                                                onChange={(e) => setActiveTab(e.target.value as 'public' | 'dev')}
                                            >
                                                <MenuItem value={'public'}>Public</MenuItem>
                                                {canAccessDevFirmware && <MenuItem value={'dev'}>Developer</MenuItem>}
                                            </Select>
                                        </FormControl>
                                    )}

                                    {firmware !== '' && (
                                        <FormControl sx={{ minWidth: 120, flex: 1 }}>
                                            <InputLabel disabled={availableFirmware.length === 0 || loading}>
                                                {firmware !== '' && [...availableFirmware.filter((f: any) => f.type === firmware)].length === 0 && !loading
                                                    ? 'No firmware found.'
                                                    : loading
                                                        ? <CircularProgress size="24px" />
                                                        : 'Version'}
                                            </InputLabel>
                                            <Select
                                                value={version}
                                                onChange={(e) => versionSelected(e.target.value)}
                                                disabled={firmwareLoading || availableFirmware.filter((f: any) => f.type === firmware).length === 0}
                                            >
                                                {availableFirmware.filter((f: any) => f.type === firmware).map((f: any) => (
                                                    <MenuItem key={f.id} value={f.version}>{f.version}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    )}
                                </Box>
                            }
                            {
                                upgradeFirmwareSubmenuOpened && command?.title === 'upgrade_firmware' &&
                                <Box sx={{ mr: 4, mt: 1, display: 'flex', flexDirection: 'row', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                                    <FormControl sx={{ minWidth: 120, flex: 1 }}>
                                        <InputLabel>Firmware</InputLabel>
                                        <Select
                                            value={firmware}
                                            onChange={(e) => firmwareSelected(e.target.value)}
                                        >
                                            <MenuItem value={'lpc'}>LPC</MenuItem>
                                            <MenuItem value={'yocto'}>Yocto</MenuItem>
                                            <MenuItem value={'spaboy'}>{palName}</MenuItem>
                                        </Select>
                                    </FormControl>

                                    {firmware !== '' && (
                                        <FormControl sx={{ minWidth: 120, flex: 1 }}>
                                            <InputLabel>Visibility</InputLabel>
                                            <Select
                                                value={activeTab}
                                                onChange={(e) => setActiveTab(e.target.value as 'public' | 'dev')}
                                            >
                                                <MenuItem value={'public'}>Public</MenuItem>
                                                {canAccessDevFirmware && <MenuItem value={'dev'}>Developer</MenuItem>}
                                            </Select>
                                        </FormControl>
                                    )}

                                    {firmware !== '' && (
                                        <FormControl sx={{ minWidth: 120, flex: 1 }}>
                                            <InputLabel disabled={availableFirmware.length === 0 || loading}>
                                                {firmware !== '' && [...availableFirmware.filter((f: any) => f.type === firmware)].length === 0 && !loading
                                                    ? 'No firmware found.'
                                                    : loading
                                                        ? <CircularProgress size="24px" />
                                                        : 'Version'}
                                            </InputLabel>
                                            <Select
                                                value={version}
                                                onChange={(e) => versionSelected(e.target.value)}
                                                disabled={firmwareLoading || availableFirmware.filter((f: any) => f.type === firmware).length === 0}
                                            >
                                                {availableFirmware.filter((f: any) => f.type === firmware).map((f: any) => (
                                                    <MenuItem key={f.id} value={f.version}>{f.version}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    )}
                                </Box>
                            }
                        </ListItem>
                       );
                    })}
                </List>
                </RadioGroup>
                {meta?.total_pages > 1 && <Pagination
                    count={meta?.total_pages} 
                    size="small"
                    onChange={(e,page)=>setPageNumber(page)}
                    page={pageNumber}
                    showLastButton 
                    showFirstButton 
                    disabled={meta?.total_pages===1}
                />}
            </div>
        :
        <div>No Commands Found.</div>
        }
    </>
  );
};

export default CommandSelect;
